import React, { useEffect, useMemo, useState, useRef } from 'react'; // NEW: useRef
import { useNavigate, useParams } from 'react-router-dom';
import axios, { PUBLIC_BASE } from '../utils/axiosInstance';
import ListCard from '../components/ListCard';

// Normalize DB image values into usable URLs.
const toImageUrl = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string' && /^https?:\/\//i.test(raw)) return raw;
  const s = String(raw);
  const path = s.startsWith('/') ? s : `/${s}`;
  return `${PUBLIC_BASE}${path}`;
};

/** Kendall-style pairwise agreement % over intersection of items. */
function similarityPercent(orderA, orderB) {
  if (!Array.isArray(orderA) || !Array.isArray(orderB)) return 0;
  const posA = new Map(orderA.map((x, i) => [String(x), i]));
  const posB = new Map(orderB.map((x, i) => [String(x), i]));
  const items = orderA.filter((x) => posB.has(String(x)));
  const m = items.length;
  if (m < 2) return (m === 0 ? 0 : 100);
  let concordant = 0;
  let total = 0;
  for (let i = 0; i < m; i++) {
    for (let j = i + 1; j < m; j++) {
      const ai = posA.get(String(items[i]));
      const aj = posA.get(String(items[j]));
      const bi = posB.get(String(items[i]));
      const bj = posB.get(String(items[j]));
      if (ai == null || aj == null || bi == null || bj == null) continue;
      total += 1;
      const sameOrder = (ai - aj) * (bi - bj) > 0;
      if (sameOrder) concordant += 1;
    }
  }
  if (total === 0) return 100;
  return Math.round((concordant / total) * 100);
}

/* Helper to show "Friends" and "Lists ranked" like UserSidebar (target user's counts) */
const FriendStats = ({ username, listsCount, align = 'center' }) => { // NEW: align prop
  const [count, setCount] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let n = null;

        // 1) Primary: target user's friends list/count
        try {
          const r = await axios.get(`/users/${encodeURIComponent(username)}/friends`);
          const arr = r.data?.friends ?? r.data?.results ?? r.data ?? [];
          if (Array.isArray(arr)) {
            n = arr.length;
          } else if (typeof r.data?.count === 'number') {
            n = r.data.count;
          } else if (typeof r.data?.friendsCount === 'number') {
            n = r.data.friendsCount;
          }
        } catch {}

        // 2) Fallback
        if (n == null) {
          try {
            const r2 = await axios.get(`/users/${encodeURIComponent(username)}`);
            const d = r2.data || {};
            n =
              (typeof d.friendsCount === 'number' && d.friendsCount) ||
              (typeof d.friendCount === 'number' && d.friendCount) ||
              (typeof d.count?.friends === 'number' && d.count.friends) ||
              null;
          } catch {}
        }

        if (alive) setCount(n);
      } catch {
        if (alive) setCount(null);
      }
    })();
    return () => { alive = false; };
  }, [username]);

  return (
    <div
      style={{
        display: 'flex',
        gap: 28,
        marginTop: 4,
        flexWrap: 'wrap',
        justifyContent: align === 'left' ? 'flex-start' : 'center' // NEW
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>
          {count ?? '—'}
        </span>
        <span style={{ fontSize: 14, opacity: 0.7, lineHeight: 1 }}>Friends</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>
          {listsCount ?? '—'}
        </span>
        <span style={{ fontSize: 14, opacity: 0.7, lineHeight: 1 }}>Lists ranked</span>
      </div>
    </div>
  );
};

/* ========================= Desktop header + divider helpers ========================= */
const Rule = () => (
  <div style={{ width: '100%', height: 1, background: 'var(--border, #e5e5e5)' }} aria-hidden="true" />
);

// Desktop-only header (image left, white panel right)
const DesktopProfileHeader = ({ friendUser, username, listsCount, style }) => { // NEW: style passthrough
  if (!friendUser) return null;

  const display = friendUser.displayName || friendUser.name || friendUser.username || username || '';
  const uname = `@${String(username || friendUser.username || '').toLowerCase()}`;
  const bio = friendUser.bio || 'No bio yet.';
  const imgSrc = friendUser.imageUrl
    ? friendUser.imageUrl
    : (friendUser.image ? toImageUrl(friendUser.image) : '');

  const CARD = {
    width: '100%',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    minHeight: 300, // NEW: taller card
    background: '#fff',
    ...style,       // NEW: apply computed width/left so it matches grid width/left
  };
  const IMG_WRAP = { background: '#0e0e0e', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const IMG = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
  const RIGHT = { padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 0, background: '#fff', textAlign: 'left' };
  const NAME = { fontSize: 22, lineHeight: '28px', fontWeight: 800 };
  const USERNAME = { fontSize: 14, color: 'var(--muted, #6b6b6b)', marginTop: 0 }; // NEW: closer to name
  const BIO = { fontSize: 14, lineHeight: '20px', color: 'var(--bw-fg-soft, #2b2b2b)', marginTop: 40 };

  return (
    <div style={{ width: '100%' }}>
      <div style={CARD}>
        <div style={IMG_WRAP}>
          {imgSrc ? (
            <img src={imgSrc} alt={display} style={IMG} />
          ) : (
            <div style={{ color: '#fff', fontWeight: 900, fontSize: 36 }}>
              {(friendUser?.username || username || '?')[0]?.toUpperCase?.() || '?'}
            </div>
          )}
        </div>
        <div style={RIGHT}>
          <div style={NAME}>{display}</div>
          <div style={USERNAME}>{uname}</div>
          <div style={BIO}>{bio}</div>
          {/* Left-aligned counts on desktop */}
          <div style={{ marginTop: 40 }}>
            <FriendStats username={username} listsCount={listsCount} align="left" /> {/* NEW */}
          </div>
        </div>
      </div>
    </div>
  );
};
/* ==================================================================================== */

const FriendRankings = () => {
  const { username } = useParams();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const [lists, setLists] = useState([]);
  const [allowed, setAllowed] = useState(null); // null=loading, true=ok, false=private
  const [scores, setScores] = useState({});     // { [listId]: number }
  const [query, setQuery] = useState('');

  // Friend profile
  const [friendUser, setFriendUser] = useState(null);

  // NEW: measure grid width/left so the header can match it (not the full divider width)
  const containerRef = useRef(null); // NEW
  const gridRef = useRef(null);      // NEW
  const [headerFit, setHeaderFit] = useState(null); // {width, ml} // NEW

  useEffect(() => {
    const sync = () => {
      if (!containerRef.current || !gridRef.current) return;
      const cont = containerRef.current.getBoundingClientRect();
      const grd  = gridRef.current.getBoundingClientRect();
      setHeaderFit({
        width: grd.width,
        ml: grd.left - cont.left
      });
    };
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []); // NEW

  useEffect(() => {
    // re-measure when lists change (grid layout may change)
    if (!containerRef.current || !gridRef.current) return;
    const id = setTimeout(() => {
      const cont = containerRef.current.getBoundingClientRect();
      const grd  = gridRef.current.getBoundingClientRect();
      setHeaderFit({ width: grd.width, ml: grd.left - cont.left });
    }, 0);
    return () => clearTimeout(id);
  }, [lists, isMobile]); // NEW

  // Load friend's list summaries (privacy-gated on the backend).
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const res = await axios.get(`/users/${encodeURIComponent(username)}/rankings`);
        if (!alive) return;
        setLists(res.data?.lists || []);
        setAllowed(true);
      } catch {
        if (!alive) return;
        setAllowed(false);
        setLists([]);
      }
    };
    run();
    return () => { alive = false; };
  }, [username]);

  // Load friend's profile (avatar + bio)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`/auth/user/${encodeURIComponent(username)}`);
        if (!alive) return;
        setFriendUser(res.data || null);
      } catch (e) {
        console.warn('Friend profile load failed:', e);
        setFriendUser(null);
      }
    })();
    return () => { alive = false; };
  }, [username]);

  // After lists load (and are allowed), fetch friend's/latest + my/latest orders to compute similarity.
  useEffect(() => {
    if (!allowed || !lists?.length) {
      setScores({});
      return;
    }
    let alive = true;

    const fetchScores = async () => {
      const results = await Promise.allSettled(
        lists.map(async (row) => {
          const id = row.listId || row.id;
          const friendRes = await axios.get(
            `/users/${encodeURIComponent(username)}/rankings/${id}`
          );
          const friendOrder = friendRes.data?.order || [];

          let myOrder = [];
          try {
            const meRes = await axios.get(`/users/me/rankings/${id}`);
            myOrder = meRes.data?.order || [];
          } catch {
            // I haven't ranked this list yet — leave empty
          }

          const bothHave = friendOrder.length > 0 && myOrder.length > 0;
          const sim = bothHave ? similarityPercent(friendOrder, myOrder) : null;
          return { id, sim, bothHave };
        })
      );

      if (!alive) return;

      const map = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          const { id, sim, bothHave } = r.value;
          if (bothHave && typeof sim === 'number') {
            map[id] = sim;
          }
        }
      });
      setScores(map);
    };

    fetchScores();
    return () => { alive = false; };
  }, [allowed, lists, username]);

  // Map API rows to ListCard props (preserves your routing + layout)
  const cards = useMemo(() => {
    return (lists || []).map((row) => {
      const id = row.listId || row.id; // support both shapes
      const score = scores[id];
      const badge = typeof score === 'number' ? `Similarity: ${score}%` : undefined;
      return {
        id,
        title: row.name || 'Untitled list',
        subtitle: row.createdBy || 'unknown',
        imageUrl: toImageUrl(row.image),
        to: `/friends/${encodeURIComponent(username)}/rankings/${id}`,
        badgeRight: badge,
      };
    });
  }, [lists, scores, username]);

  // Live filter by title (case-insensitive)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => (c.title || '').toLowerCase().includes(q));
  }, [cards, query]);

  const onClear = () => setQuery('');

  // (old) searchRowMargin left as-is for mobile; desktop uses new layout below

  return (
    <div className="scroll-shell">
      <div className="scroll-body">
        <div ref={containerRef} style={{ maxWidth: 980, margin: '0 auto' }}>{/* NEW: ref here */}

          {/* Back + Chat grouped together on MOBILE; Back alone on DESKTOP (Chat stays in search row) */}
          {isMobile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <button
                className="logout-btn back-btn"
                onClick={() => navigate('/friends')}
              >
                ← Back
              </button>
              <button
                className="btn-outline-black"
                onClick={() => navigate(`/friends/${encodeURIComponent(username)}/chat`)}
                style={{ padding: '8px 14px' }}
              >
                Chat
              </button>
            </div>
          ) : (
            <>
              <button
                className="logout-btn back-btn"
                onClick={() => navigate('/friends')}
                style={{ marginBottom: 10 }}
              >
                ← Back
              </button>
            </>
          )}

          {/* Title (desktop only) — REMOVED per request */} {/* NEW: removed big @USERNAME header */}

          {/* Friend profile */}
          {friendUser && (
            <div
              style={{
                display: 'grid',
                placeItems: 'center',
                margin: isMobile ? '8px 0 20px' : '6px 0 28px'
              }}
            >
              {/* MOBILE: unchanged */}
              {isMobile ? (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 520,
                    display: 'grid',
                    gap: 0,
                    justifyItems: 'center',
                    textAlign: 'center'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      border: '2px solid #0e0e0e',
                      borderRadius: 20,
                      background: '#fff',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {(() => {
                      const src = (friendUser?.imageUrl) ? friendUser.imageUrl
                        : (friendUser?.image ? `${PUBLIC_BASE}${String(friendUser.image).startsWith('/') ? friendUser.image : '/' + friendUser.image}` : '');
                      return src ? (
                        <img src={src} alt={`@${username}'s avatar`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                      ) : (
                        <div style={{ fontWeight: 900, fontSize: 28 }}>
                          {(friendUser?.username || username || '?')[0]?.toUpperCase?.() || '?'}
                        </div>
                      );
                    })()}
                  </div>

                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 'clamp(1.25rem, 1.05rem + .4vw, 1.5rem)',
                      marginTop: 8,
                      marginBottom: 0
                    }}
                  >
                    {friendUser?.displayName || friendUser?.name || String(username).toUpperCase()}
                  </div>

                  <div style={{ color: '#0e0e0e', opacity: 0.8, fontSize: 16, fontWeight: 600, marginTop: 0 }}>
                    {`@${String(username).toLowerCase()}`}
                  </div>

                  <div style={{ marginTop: 20, marginBottom: 20, fontSize: 14, lineHeight: 1.5 }}>
                    {friendUser?.bio ? friendUser.bio : 'No bio yet.'}
                  </div>

                  <FriendStats username={username} listsCount={(lists || []).length} />
                </div>
              ) : (
                // DESKTOP: new header, lengthened, and sized to the GRID width (not divider)
                <DesktopProfileHeader
                  friendUser={friendUser}
                  username={username}
                  listsCount={(lists || []).length}
                  style={headerFit ? { width: headerFit.width, marginLeft: headerFit.ml } : undefined} // NEW
                />
              )}
            </div>
          )}

          {allowed === false ? (
            <div style={{ textAlign: 'center', marginTop: 12, color: '#444' }}>
              You’ll be able to see @{username}&apos;s rankings once you’re friends.
              Send a request from the <b>Friends</b> tab.
            </div>
          ) : (
            <>
              {/* Keep divider full-width */}
              {!isMobile && <Rule />}

              {/* Search row (still full width with Clear + Chat) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  margin: '14px 0',
                }}
              >
                <input
                  className="input input-outline-black"
                  type="text"
                  value={query}
                  placeholder="Search ranked lists by name…"
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search friend's lists"
                  style={{ height: 40, flex: 1 }}
                />
                <button
                  className="btn-outline-black"
                  onClick={onClear}
                  disabled={!query}
                  style={{ height: 40, padding: '0 14px' }}
                >
                  Clear
                </button>
                {!isMobile && (
                  <button
                    className="btn-outline-black"
                    onClick={() => navigate(`/friends/${encodeURIComponent(username)}/chat`)}
                    style={{ height: 40, padding: '0 14px' }}
                  >
                    Chat
                  </button>
                )}
              </div>

              <Rule />

              {/* GRID: give me a ref so header can copy my width/left */}
              <div className="grid" ref={gridRef}> {/* NEW: ref */}
                {filtered.map((c) => (
                  <ListCard
                    key={c.id}
                    id={c.id}
                    title={c.title}
                    subtitle={c.subtitle}
                    imageUrl={c.imageUrl}
                    to={c.to}
                    badgeRight={c.badgeRight}
                  />
                ))}
              </div>

              {allowed && !filtered?.length && (
                <div style={{ textAlign: 'center', color: '#777', marginTop: 24 }}>
                  {query ? <>No lists match “{query}”.</> : <>This user hasn’t ranked any lists yet.</>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendRankings;
